import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { selectAccountData } from "../../redux/main/accountSlice";
import { useColorPalette } from "../../hooks/useColorPalette";
import { closeModal, selectRightModalIsOpen, selectRightModalmode, setModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";
import DynamicIcon from "../DynamicIcon";
import Error from "../Error";
import CustomFolder from "./CustomFolder";
import moment from "moment";
import axios from "axios";
import 'moment/locale/it';
import { getNodeApiUrl } from "../../config/api.config";
import AppointmentCard from "../AppointmentCard";
import AddEditSystem from "../systemsPage/AddEditSystem";
import JobCard from "../systemsPage/JobCard";
import AssistanceCard from "../systemsPage/AssistanceCard";

// Componente per mostrare testo espandibile
const ReadMoreText = ({ text, wordLimit }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (!text) return null;
    
    const words = text.split(' ');
    const needsReadMore = words.length > wordLimit;
    
    const toggleExpand = () => setIsExpanded(!isExpanded);
    
    return (
        <div>
            <p className="text-sm">
                {isExpanded || !needsReadMore ? text : `${words.slice(0, wordLimit).join(' ')}...`}
            </p>
            {needsReadMore && (
                <button 
                    onClick={toggleExpand}
                    className="text-xs text-blue-600 hover:underline mt-1"
                >
                    {isExpanded ? 'Leggi meno' : 'Leggi di più'}
                </button>
            )}
        </div>
    );
};

// Componente per visualizzare una coppia etichetta-valore con stile
const DetailItem = ({ label, value, palette, isExpandable = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const MAX_WORDS = 30; // Numero di parole da visualizzare prima del troncamento

    const toggleIsExpanded = (e) => {
        e.preventDefault();
        setIsExpanded(!isExpanded);
    };

    const renderValue = () => {
        if (isExpandable && typeof value === 'string') {
            const words = value.split(/\s+/); // Splitta per spazi multipli o singoli
            if (words.length > MAX_WORDS) {
                const linkStyle = { color: palette.primary || '#007bff', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.75rem', display: 'inline-block', marginLeft: '4px' };
                if (isExpanded) {
                    return (
                        <>
                            {value}
                            <span onClick={toggleIsExpanded} style={linkStyle} role="button" tabIndex={0} onKeyPress={(e) => e.key === 'Enter' && toggleIsExpanded(e)}>
                                Leggi di meno
                            </span>
                        </>
                    );
                }
                return (
                    <>
                        {words.slice(0, MAX_WORDS).join(' ')}...
                        <span onClick={toggleIsExpanded} style={linkStyle} role="button" tabIndex={0} onKeyPress={(e) => e.key === 'Enter' && toggleIsExpanded(e)}>
                            Leggi di più
                        </span>
                    </>
                );
            }
        }
        return value || '--';
    };

    return (
        <div className="py-2">
            <p className="text-sm font-medium" style={{ color: palette.textOnBackground, opacity: 0.7 }}>{label}</p>
            <div className="text-base font-semibold" style={{ color: palette.textOnBackground, whiteSpace: 'pre-wrap' }}>
                {renderValue()}
            </div>
        </div>
    );
};


export default function ViewCustomer({ customerId }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const accountData = useSelector(selectAccountData);
    const palette = useColorPalette();
    
    // Stato per la gestione del pannello di modifica impianto
    const [editingSystemId, setEditingSystemId] = useState(null);
    const token = localStorage.getItem('installamelo-auth-token');
    
    // Stati principali
    const [customerData, setCustomerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    
    // Stati per i dati dei vari tab
    const [addresses, setAddresses] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [systems, setSystems] = useState(null);
    const [jobs, setJobs] = useState(null);
    const [assistanceRequests, setAssistanceRequests] = useState(null);
    const [maintenances, setMaintenances] = useState(null);
    const [appointments, setAppointments] = useState({ loading: false, data: [], error: null });
    
    // Stati per indicare il caricamento di ciascun tab
    const [addressesLoading, setAddressesLoading] = useState(false);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [systemsLoading, setSystemsLoading] = useState(false);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [assistanceRequestsLoading, setAssistanceRequestsLoading] = useState(false);
    const [maintenancesLoading, setMaintenancesLoading] = useState(false);
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);

    // Funzione per generare i titoli delle tab con conteggio elementi
    const getTabTitlesWithCount = () => [
        `Indirizzi${addresses && addresses.length > 0 ? ` (${addresses.length})` : ""}`,
        `Contatti${contacts && contacts.length > 0 ? ` (${contacts.length})` : ""}`,
        `Impianti${systems && systems.length > 0 ? ` (${systems.length})` : ""}`,
        `Commesse${jobs && jobs.length > 0 ? ` (${jobs.length})` : ""}`,
        `ASSISTENZA${assistanceRequests && assistanceRequests.length > 0 ? ` (${assistanceRequests.length})` : ""}`,
        `Manutenzioni${maintenances && maintenances.length > 0 ? ` (${maintenances.length})` : ""}`,
        `Appuntamenti${appointments?.data && appointments.data.length > 0 ? ` (${appointments.data.length})` : ""}`
    ];
    
    // State per i titoli delle tab
    const [tabTitles, setTabTitles] = useState(getTabTitlesWithCount());
    
    // Aggiorna i titoli delle tab quando cambiano i dati
    useEffect(() => {
        setTabTitles(getTabTitlesWithCount());
    }, [addresses, contacts, systems, jobs, assistanceRequests, maintenances, appointments?.data]);
    
    // Funzione per recuperare i dati del cliente
    const fetchCustomerData = async () => {
        if (!customerId) {
            setError("ID cliente non valido");
            setLoading(false);
            return;
        }
        
        setLoading(true);
        
        try {
            const apiUrl = getNodeApiUrl(`/customers?customerId=${customerId}`);
            const response = await fetch(apiUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                throw new Error(`Errore nel recupero dei dati del cliente: ${response.status}`);
            }
            
            const data = await response.json();
            setCustomerData(data);
            
            // Estrarre indirizzi e contatti per i rispettivi tab
            if (data.addresses) setAddresses(data.addresses);
            if (data.contactsList) setContacts(data.contactsList);
            
            setLoading(false);
        } catch (err) {
            console.error('[ViewCustomer] Errore fetchCustomerData:', err);
            setError(err.message);
            setLoading(false);
        }
    };
    
    // Funzione per recuperare gli impianti del cliente
    const fetchSystems = async () => {
        if (systemsLoading || !customerId || !accountData?._id) return;
        
        setSystemsLoading(true);
        try {
            const apiUrl = getNodeApiUrl(`/systems/systemsList?accountId=${accountData._id}&customerId=${customerId}`);
            console.log('Chiamata a systems API:', apiUrl);
            
            const response = await fetch(apiUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Errore API ${response.status}:`, errorText);
                throw new Error(`Errore nel recupero degli impianti (${response.status})`);
            }
            
            const data = await response.json();
            setSystems(data);
            setSystemsLoading(false);
        } catch (err) {
            console.error('[ViewCustomer] Errore fetchSystems:', err.message || err);
            setSystemsLoading(false);
        }
    };
    
    // Funzione per recuperare le commesse del cliente
    const fetchJobs = async () => {
        console.log('[DEBUG] Esecuzione fetchJobs - stato iniziale:', { jobsLoading, customerId, accountId: accountData?._id });
        if (!customerId || !accountData?._id) {
            console.warn('[ViewCustomer] Impossibile recuperare commesse: dati mancanti', { customerId, accountId: accountData?._id });
            return;
        }
        
        // Reset dello stato di caricamento
        setJobsLoading(true);
        try {
            const apiUrl = getNodeApiUrl(`/jobs/list?accountId=${accountData._id}&customerId=${customerId}`);
            
            const response = await fetch(apiUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Errore API ${response.status}:`, errorText);
                throw new Error(`Errore nel recupero delle commesse (${response.status})`);
            }
            
            const data = await response.json();
            console.log('[DEBUG] Struttura dati commesse:', data);
            
            // Verifica la struttura dei dati ricevuti
            if (Array.isArray(data)) {
                // Se è già un array, lo usa direttamente
                setJobs(data);
            } else if (data && data.data && Array.isArray(data.data)) {
                // Se è un oggetto con un campo 'data' contenente l'array
                setJobs(data.data);
            } else {
                // Se non riusciamo a trovare un array valido
                console.error('[ViewCustomer] Struttura dati commesse non valida:', data);
                setJobs([]);
            }
            
            setJobsLoading(false);
        } catch (err) {
            console.error('[ViewCustomer] Errore fetchJobs:', err.message || err);
            setJobsLoading(false);
            setJobs([]);
        }
    };
    
    // Funzione per recuperare le richieste di assistenza
    const fetchAssistanceRequests = async () => {
        console.log('[DEBUG] Esecuzione fetchAssistanceRequests - stato iniziale:', { assistanceRequestsLoading, customerId, accountId: accountData?._id });
        if (!customerId || !accountData?._id) {
            console.warn('[ViewCustomer] Impossibile recuperare richieste assistenza: dati mancanti', { customerId, accountId: accountData?._id });
            return;
        }
        
        // Reset dello stato di caricamento
        setAssistanceRequestsLoading(true);
        try {
            // Endpoint delle richieste di assistenza - corretto come in ViewSystem.jsx
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
            console.log('[DEBUG] Chiamata endpoint assistenze:', `${apiBaseUrl}/assistanceRequests/list`, { accountId: accountData._id, customerId });
            
            // Utilizzo axios come in ViewSystem
            const response = await axios.get(`${apiBaseUrl}/assistanceRequests/list`, {
                headers: {
                    'auth-token': token
                },
                params: {
                    accountId: accountData._id,
                    customerId: customerId
                }
            });
            
            const data = response.data;
            console.log('[DEBUG] Struttura dati assistanceRequests:', data);
            
            // Verifica la struttura dei dati ricevuti
            if (Array.isArray(data)) {
                // Se è già un array, lo usa direttamente
                setAssistanceRequests(data);
            } else if (data && data.data && Array.isArray(data.data)) {
                // Se è un oggetto con un campo 'data' contenente l'array
                setAssistanceRequests(data.data);
            } else {
                // Se non riusciamo a trovare un array valido
                console.error('[ViewCustomer] Struttura dati richieste non valida:', data);
                setAssistanceRequests([]);
            }
            
            setAssistanceRequestsLoading(false);
        } catch (err) {
            console.error('[ViewCustomer] Errore fetchAssistanceRequests:', err.message || err);
            setAssistanceRequestsLoading(false);
            setAssistanceRequests([]);
        }
    };
    
    // Funzione per recuperare le manutenzioni
    const fetchMaintenances = async () => {
        if (maintenancesLoading || !customerId || !accountData?._id) return;
        
        setMaintenancesLoading(true);
        try {
            const apiUrl = getNodeApiUrl(`/maintenances/list?accountId=${accountData._id}&customerId=${customerId}`);
            console.log('Chiamata a maintenances API:', apiUrl);
            
            const response = await fetch(apiUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Errore API ${response.status}:`, errorText);
                throw new Error(`Errore nel recupero delle manutenzioni (${response.status})`);
            }
            
            const data = await response.json();
            setMaintenances(data);
            setMaintenancesLoading(false);
        } catch (err) {
            console.error('[ViewCustomer] Errore fetchMaintenances:', err.message || err);
            setMaintenancesLoading(false);
        }
    };
    
    // Funzione per recuperare gli appuntamenti del cliente
    const fetchAppointments = async () => {
        if (appointmentsLoading || !customerId || !accountData?._id) return;
        
        setAppointmentsLoading(true);
        try {
            const apiUrl = getNodeApiUrl(`/appointments/bycustomer?accountId=${accountData._id}&customerId=${customerId}`);
            console.log('Chiamata a appointments API:', apiUrl);
            const response = await fetch(apiUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                throw new Error(`Errore nel recupero degli appuntamenti: ${response.status}`);
            }
            
            const data = await response.json();
            setAppointments({
                loading: false,
                data: data,
                error: null
            });
            setAppointmentsLoading(false);
        } catch (err) {
            console.error('[ViewCustomer] Errore fetchAppointments:', err);
            setAppointmentsLoading(false);
            setAppointments({
                loading: false,
                data: [],
                error: err.message
            });
        }
    };
    
    // Hook per caricare i dati del cliente e tutti i dati correlati all'inizializzazione
    useEffect(() => {
        if (customerId && token) {
            // Prima carica i dati del cliente
            fetchCustomerData();
            
            // Poi, se abbiamo anche l'accountId, carichiamo tutti gli altri dati
            if (accountData?._id) {
                console.log('[DEBUG] Caricamento dati associati al cliente:', customerId);
                // Carica tutti i dati all'avvio per avere i conteggi nelle tab
                // Esecuzione in serie per evitare problemi di stato
                fetchSystems();
                
                // Commesse - assicuriamoci che venga chiamato
                console.log('[DEBUG] Chiamata fetchJobs per cliente:', customerId);
                try {
                    fetchJobs();
                } catch (error) {
                    console.error('[ViewCustomer] Errore durante fetchJobs:', error);
                }
                
                // Richieste assistenza - assicuriamoci che venga chiamato
                console.log('[DEBUG] Chiamata fetchAssistanceRequests per cliente:', customerId);
                try {
                    fetchAssistanceRequests();
                } catch (error) {
                    console.error('[ViewCustomer] Errore durante fetchAssistanceRequests:', error);
                }
                
                fetchMaintenances();
                fetchAppointments();
            }
        }
    }, [customerId, token, accountData?._id]);

    // Funzioni di rendering

    // Rendering Tab Indirizzi
    const renderAddressesTab = () => {
        if (addressesLoading) {
            return <LoadingSection />;
        }

        if (!addresses || addresses.length === 0) {
            return <p className="p-4 text-center text-gray-500">Nessun indirizzo registrato</p>;
        }

        return (
            <div className="space-y-6">
                {addresses.map((address, index) => {
                    // Calcola tempo e distanza in formato leggibile
                    const distanceKm = address.geoData?.distanceKm ? 
                        address.geoData.distanceKm < 10 ? 
                            Math.round(address.geoData.distanceKm * 10) / 10 : 
                            Math.round(address.geoData.distanceKm) : null;
                    
                    const distanceTime = address.geoData?.distanceTime ? 
                        address.geoData.distanceTime < 60 ? 
                            `${Math.round(address.geoData.distanceTime)} min` : 
                            `${Math.round(address.geoData.distanceTime / 60 * 10) / 10} ore` : null;
                    
                    return (
                        <div key={address._id || index} className="bg-white p-4 rounded-md shadow-md border border-gray-100">
                            <div className="flex justify-between items-start">
                                <h4 className="font-medium text-lg mb-2" style={{ color: palette.primary }}>
                                    {address.addressTypeLabel || (address.fiscal ? 'Sede Legale' : 'Sede Operativa')}
                                    {address.fiscal && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Fiscale</span>}
                                </h4>
                            </div>

                            <div className="mt-3 space-y-3">
                                {/* Indirizzo */}
                                <div className="flex items-start">
                                    <i className="fas fa-map-marker-alt mt-1 mr-3" style={{color: palette.primary}}></i>
                                    <div>
                                        <p className="font-medium">{address.address}</p>
                                        {(distanceKm || distanceTime) && (
                                            <p className="text-sm text-gray-500 mt-1">
                                                {distanceKm && <span><i className="fas fa-road mr-1"></i> {distanceKm} km</span>}
                                                {distanceKm && distanceTime && <span className="mx-2">|</span>}
                                                {distanceTime && <span><i className="fas fa-clock mr-1"></i> {distanceTime}</span>}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Contatti */}
                                {(address.email || address.phone || address.mobile) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                        {address.email && (
                                            <div className="flex items-center">
                                                <i className="fas fa-envelope mr-3" style={{color: palette.primary}}></i>
                                                <span>{address.email}</span>
                                            </div>
                                        )}
                                        {address.phone && (
                                            <div className="flex items-center">
                                                <i className="fas fa-phone mr-3" style={{color: palette.primary}}></i>
                                                <span>{address.phone}</span>
                                            </div>
                                        )}
                                        {address.mobile && (
                                            <div className="flex items-center">
                                                <i className="fas fa-mobile-alt mr-3" style={{color: palette.primary}}></i>
                                                <span>{address.mobile}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Rendering Tab Contatti
    const renderContactsTab = () => {
        if (contactsLoading) {
            return <LoadingSection />;
        }

        if (!contacts || contacts.length === 0) {
            return <p className="p-4 text-center text-gray-500">Nessun contatto registrato</p>;
        }

        return (
            <div className="space-y-4">
                {contacts.map((contact, index) => (
                    <div key={contact._id || index} className="bg-white p-4 rounded-md shadow">
                        <h4 className="font-medium text-lg mb-2 flex items-center gap-2" style={{ color: palette.primary }}>
                            {contact.fullName || [contact.name, contact.surname].filter(Boolean).join(' ')}
                            {contact.main && (
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-normal">
                                    <i className="fa-solid fa-star mr-1"></i>Principale
                                </span>
                            )}
                        </h4>
                        <div className="space-y-2">
                            {contact.role && <p className="text-sm font-medium">{contact.role}</p>}
                            {contact.email && <p><i className="fas fa-envelope mr-2" style={{color: palette.primary}}></i> {contact.email}</p>}
                            {contact.phone && <p><i className="fas fa-phone mr-2" style={{color: palette.primary}}></i> {contact.phone}</p>}
                            {contact.notes && <p className="text-sm italic mt-2">{contact.notes}</p>}
                        </div>
                    </div>
                ))}
            </div>
        );
    };
    
    // Rendering del contenuto in base al tab attivo
    const renderTabContent = () => {
        switch (activeTab) {
            case 0: // Indirizzi
                return renderAddressesTab();
            case 1: // Contatti
                return renderContactsTab();
            case 2: // Impianti
                return (
                    systemsLoading ? <LoadingSection /> : 
                    !systems || systems.length === 0 ? 
                    <p className="p-4 text-center text-gray-500">Nessun impianto registrato</p> :
                    <div className="space-y-4 p-4">
                        {systems.map((system) => (
                            <div key={system._id} className="bg-white shadow-md rounded-lg overflow-hidden border-l-4" style={{ borderLeftColor: palette.primary }}>
                                <div className="p-4">
                                    <div className="flex justify-between items-start">
                                        <h3 className="text-lg font-bold" style={{ color: palette.primary }}>{system.title}</h3>
                                        <div className="text-xs bg-gray-100 px-2 py-1 rounded">
                                            ID: {system._id}
                                        </div>
                                    </div>
                                    
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Indirizzo */}
                                        <div className="flex items-start space-x-2">
                                            <i className="fas fa-map-marker-alt mt-1" style={{ color: palette.secondary }}></i>
                                            <div>
                                                <p className="text-sm font-medium">Indirizzo</p>
                                                <p>{system.customerAddressLabel || "Non specificato"}</p>
                                            </div>
                                        </div>
                                        
                                        {/* Data creazione */}
                                        <div className="flex items-start space-x-2">
                                            <i className="fas fa-calendar-alt mt-1" style={{ color: palette.secondary }}></i>
                                            <div>
                                                <p className="text-sm font-medium">Data creazione</p>
                                                <p>{system.creationDate ? moment(system.creationDate).format('DD/MM/YYYY') : "Non specificata"}</p>
                                            </div>
                                        </div>
                                        
                                        {/* Distanza */}
                                        {system.geoData?.distanceKm && (
                                            <div className="flex items-start space-x-2">
                                                <i className="fas fa-route mt-1" style={{ color: palette.secondary }}></i>
                                                <div>
                                                    <p className="text-sm font-medium">Distanza</p>
                                                    <p>{Math.round(system.geoData.distanceKm)} km ({Math.round(system.geoData.distanceTime)} min)</p>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Tipi di impianto */}
                                        <div className="flex items-start space-x-2 sm:col-span-2">
                                            <i className="fas fa-tag mt-1" style={{ color: palette.secondary }}></i>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium">Tipi di impianto</p>
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {system.typesList && system.typesList.length > 0 ? (
                                                        system.typesList.map((type, idx) => (
                                                            <span 
                                                                key={type._id || idx} 
                                                                className="bg-gray-100 text-gray-800 text-sm px-2 py-1 rounded-full"
                                                            >
                                                                {type.typeLabel}
                                                                {type.expirationWarrantyDate && (
                                                                    <span className="text-xs ml-1 text-gray-500">
                                                                        (scad. {moment(type.expirationWarrantyDate).format('DD/MM/YYYY')})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-gray-500">Nessun tipo specificato</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Note */}
                                        {system.notes && (
                                            <div className="flex items-start space-x-2 sm:col-span-2 mt-2">
                                                <i className="fas fa-sticky-note mt-1" style={{ color: palette.secondary }}></i>
                                                <div>
                                                    <p className="text-sm font-medium">Note</p>
                                                    <p className="whitespace-pre-wrap">{system.notes}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="flex justify-end mt-4">
                                        <button 
                                            onClick={() => {
                                                // Imposta l'ID dell'impianto da modificare
                                                setEditingSystemId(system._id);
                                            }}
                                            className="text-sm py-1 px-3 rounded" 
                                            style={{ backgroundColor: palette.primary, color: 'white' }}
                                        >
                                            <i className="fas fa-pencil-alt mr-2"></i>
                                            Modifica impianto
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            case 3: // Commesse
                return (
                    jobsLoading ? <LoadingSection /> : 
                    !jobs || !Array.isArray(jobs) || jobs.length === 0 ? 
                    <p className="p-4 text-center text-gray-500">Nessuna commessa registrata</p> :
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {jobs.map(job => (
                            <JobCard
                                key={job._id}
                                job={job}
                                accountId={accountData?._id}
                                token={token}
                                apiBaseUrl={import.meta.env.VITE_API_BASE_URL}
                            />
                        ))}
                    </div>
                );
            case 4: // Richieste Assistenza
                return (
                    assistanceRequestsLoading ? <LoadingSection /> : 
                    !assistanceRequests || !Array.isArray(assistanceRequests) || assistanceRequests.length === 0 ? 
                    <p className="p-4 text-center text-gray-500">Nessuna richiesta di assistenza</p> :
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {assistanceRequests.map(request => (
                            <AssistanceCard
                                key={request._id}
                                assistance={request}
                                accountId={accountData?._id}
                                token={token}
                                apiBaseUrl={import.meta.env.VITE_API_BASE_URL}
                            />
                        ))}
                    </div>
                );
            case 5: // Manutenzioni
                return (
                    maintenancesLoading ? <LoadingSection /> : 
                    !maintenances || maintenances.length === 0 ? 
                    <p className="p-4 text-center text-gray-500">Nessuna manutenzione registrata</p> :
                    <div className="space-y-4">
                        {/* Contenuto per le manutenzioni */}
                    </div>
                );
            case 6: // Appuntamenti
                return (
                    appointmentsLoading ? <LoadingSection /> : 
                    !appointments.data || appointments.data.length === 0 ? 
                    <p className="p-4 text-center text-gray-500">Nessun appuntamento registrato</p> :
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[...appointments.data]
                            .sort((a, b) => (new Date(b.fromDate || 0) - new Date(a.fromDate || 0))) /* Dal più recente al più vecchio */
                            .map((appointment) => (
                                <AppointmentCard 
                                    key={appointment._id}
                                    appointment={appointment}
                                    showDetails={true}
                                    palette={palette}
                                    token={token}
                                    apiBaseUrl={import.meta.env.VITE_API_BASE_URL}
                                />
                            ))}
                    </div>
                );
            default:
                return <p>Seleziona un tab</p>;
        }
    };
    
    // Funzione per gestire la chiusura del pannello
    const handleClosePanel = () => {
        dispatch(closeModal());
    };

    // Funzione per gestire il completamento della modifica dell'impianto
    const handleSystemEditComplete = () => {
        // Aggiorna i dati degli impianti per riflettere le modifiche
        fetchSystems();
        // Ripristina la visualizzazione normale
        setEditingSystemId(null);
    };

    // Rendering principale
    return (
        <div className="p-4 sm:p-6 md:p-8 flex flex-col xl:flex-row gap-6 bg-gray-50 min-h-full">
            {loading ? (
                <LoadingSection />
            ) : error ? (
                <Error message={error} />
            ) : editingSystemId ? (
                // Visualizzazione pannello modifica impianto
                <div className="bg-white shadow-xl rounded-lg overflow-hidden p-4 w-full">
                    <div className="flex justify-between items-center mb-6 pb-3 border-b">
                        <h3 className="text-lg font-bold flex items-center" style={{ color: palette.primary }}>
                            <DynamicIcon iconClass="fa-solid fa-tools" /> 
                            <span className="ml-2">Modifica Impianto</span>
                        </h3>
                        <button
                            onClick={() => setEditingSystemId(null)}
                            className="flex items-center text-gray-600 hover:text-gray-800"
                        >
                            <i className="fas fa-arrow-left mr-2"></i>
                            Torna al cliente
                        </button>
                    </div>
                    <AddEditSystem 
                        systemId={editingSystemId}
                        onSaveComplete={handleSystemEditComplete}
                    />
                </div>
            ) : (
                <>
                    {/* Colonna Sinistra - Dettagli Cliente */}
                    <div className="bg-white shadow-xl rounded-lg overflow-hidden flex flex-col gap-y-6 w-full xl:w-1/4 h-fit" 
                         style={{ borderTop: `4px solid ${palette.primary}` }}>
                        
                        <div style={{ backgroundColor: palette.tableHeaderBg, borderBottom: '1px solid #eee' }} className="p-4">
                            <h3 className="text-lg font-bold flex items-center" style={{ color: palette.tableHeaderText }}>
                                <DynamicIcon iconClass="fa-solid fa-user" /> 
                                <span className="ml-5">Dettagli Cliente</span>
                            </h3>
                            {customerData?._id && (
                                <p className="text-xs font-light mt-1" style={{ color: palette.tableHeaderText, opacity: 0.8 }}>
                                    ID: {customerData._id}
                                </p>
                            )}
                        </div>
                        
                        <div className="px-6 pt-4 space-y-4">
                            <div>
                                <p className="text-xs font-medium" style={{ color: palette.neutralText }}>Cliente</p>
                                <div className="flex items-center justify-between mt-1">
                                    <div className="flex items-center">
                                        <i className={`mr-2 ${customerData?.business ? 'fas fa-industry' : 'fas fa-building'}`} 
                                           style={{ color: palette.primary }}></i>
                                        <p className="text-lg font-bold" style={{ color: palette.text }}>
                                            {customerData?.business 
                                                ? customerData?.company 
                                                : `${customerData?.name || ''} ${customerData?.surname || ''}`}
                                        </p>
                                    </div>
                                    <span className="text-xs px-2 py-1 rounded-full" 
                                        style={{
                                            backgroundColor: customerData?.active ? palette.successBg : palette.errorBg,
                                            color: customerData?.active ? palette.successText : palette.errorText
                                        }}>
                                        {customerData?.active ? "Attivo" : "Inattivo"}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 gap-x-6 gap-y-4">
                                {customerData?.businessName && (
                                    <DetailItem label="Ragione Sociale" value={customerData.businessName} palette={palette} />
                                )}
                                {customerData?.type && (
                                    <DetailItem label="Tipologia" value={customerData.type === 'business' ? 'Azienda' : 'Privato'} palette={palette} />
                                )}
                                {customerData?.vatNumber && (
                                    <DetailItem label="Partita IVA" value={customerData.vatNumber} palette={palette} />
                                )}
                                {customerData?.fiscalCode && (
                                    <DetailItem label="Codice Fiscale" value={customerData.fiscalCode} palette={palette} />
                                )}
                                {customerData?.email && (
                                    <DetailItem label="Email" value={customerData.email} palette={palette} />
                                )}
                                {customerData?.phone && (
                                    <DetailItem label="Telefono" value={customerData.phone} palette={palette} />
                                )}
                                
                                {/* Indirizzo */}
                                {addresses && addresses.length > 0 && (() => {
                                    const fiscalAddress = addresses.find(addr => addr.fiscal === true) || addresses[0];
                                    const hasGeoData = fiscalAddress.geoData?.distanceKm !== undefined || fiscalAddress.distanceKm !== undefined;
                                    
                                    return (
                                        <>
                                            <div className="border-t border-gray-200 pt-3 mt-2">
                                                <h4 className="font-semibold mb-2 flex items-center" style={{ color: palette.primary }}>
                                                    <i className="fas fa-map-marker-alt mr-2"></i>
                                                    Indirizzo
                                                </h4>
                                            </div>
                                            <DetailItem 
                                                label={fiscalAddress.addressTypeLabel || "Indirizzo"} 
                                                value={fiscalAddress.address || `${fiscalAddress.street || ''} ${fiscalAddress.number || ''}, ${fiscalAddress.zipCode || ''} ${fiscalAddress.city || ''} ${fiscalAddress.province ? `(${fiscalAddress.province})` : ''}`}
                                                palette={palette} 
                                            />
                                            {hasGeoData && (
                                                <div className="ml-4 mt-1 text-sm">
                                                    {(fiscalAddress.geoData?.distanceKm !== undefined || fiscalAddress.distanceKm !== undefined) && (
                                                        <p className="text-gray-700">
                                                            <i className="fas fa-route mr-2" style={{color: palette.secondary}}></i>
                                                            Distanza: <span className="font-medium">
                                                                {Math.round(fiscalAddress.geoData?.distanceKm || fiscalAddress.distanceKm)} km
                                                            </span>
                                                        </p>
                                                    )}
                                                    {(fiscalAddress.geoData?.distanceTime !== undefined || fiscalAddress.distanceTime !== undefined) && (
                                                        <p className="text-gray-700 mt-1">
                                                            <i className="fas fa-clock mr-2" style={{color: palette.secondary}}></i>
                                                            Tempo: <span className="font-medium">
                                                                {Math.round(fiscalAddress.geoData?.distanceTime || fiscalAddress.distanceTime)} min
                                                            </span>
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                                
                                {/* Contatto principale: quello segnato, altrimenti il primo (stessa regola delle mail di assistenza) */}
                                {contacts && contacts.length > 0 && (() => {
                                    const mainContact = contacts.find(c => c.main === true) || contacts[0];
                                    const firstContact = {
                                        ...mainContact,
                                        fullName: mainContact.fullName || [mainContact.name, mainContact.surname].filter(Boolean).join(' ')
                                    };
                                    return (
                                        <>
                                            <div className="border-t border-gray-200 pt-3 mt-2">
                                                <h4 className="font-semibold mb-2 flex items-center" style={{ color: palette.primary }}>
                                                    <i className="fas fa-user-tie mr-2"></i>
                                                    Contatto Principale
                                                </h4>
                                            </div>
                                            {firstContact.fullName && (
                                                <DetailItem label="Nome" value={firstContact.fullName} palette={palette} />
                                            )}
                                            {firstContact.role && (
                                                <DetailItem label="Ruolo" value={firstContact.role} palette={palette} />
                                            )}
                                            {firstContact.email && (
                                                <DetailItem label="Email" value={firstContact.email} palette={palette} />
                                            )}
                                            {firstContact.phone && (
                                                <DetailItem label="Telefono" value={firstContact.phone} palette={palette} />
                                            )}
                                        </>
                                    );
                                })()}
                                
                                {customerData?.notes && (
                                    <>
                                        <div className="border-t border-gray-200 pt-3 mt-2">
                                            <h4 className="font-semibold mb-2 flex items-center" style={{ color: palette.primary }}>
                                                <i className="fas fa-sticky-note mr-2"></i>
                                                Note
                                            </h4>
                                        </div>
                                        <DetailItem label="Note Cliente" value={customerData.notes} palette={palette} isExpandable={true} />
                                    </>
                                )}
                                
                                {/* Linea separatrice e pulsanti in basso */}
                                <div className="border-t border-gray-200 mt-6 pt-4"></div>
                                <div className="flex justify-center gap-3 px-4 pb-4">
                                    <button
                                        onClick={() => navigate(`/clienti/edit/${customerId}`)}
                                        className="py-2 px-4 rounded text-white font-medium flex items-center justify-center"
                                        aria-label="Modifica Cliente"
                                        title="Modifica Cliente"
                                        style={{ backgroundColor: palette.primary }}
                                    >
                                        <i className="fas fa-pencil-alt mr-2"></i>
                                        Modifica Cliente
                                    </button>
                                    <button
                                        onClick={() => {
                                            const label = customerData?.business
                                                ? customerData?.company
                                                : [customerData?.name, customerData?.surname].filter(Boolean).join(' ');
                                            navigate('/leadsList', {
                                                state: {
                                                    openNewLead: true,
                                                    prefillFromCustomer: {
                                                        customerId: customerData?._id,
                                                        label,
                                                        isProspect: !!customerData?.prospect,
                                                        business: !!customerData?.business,
                                                        company: customerData?.company || '',
                                                        name: customerData?.name || '',
                                                        surname: customerData?.surname || '',
                                                        phone: customerData?.phone || '',
                                                        email: customerData?.email || '',
                                                        address: addresses?.[0]?.address || '',
                                                        city: addresses?.[0]?.city || '',
                                                        province: addresses?.[0]?.province || '',
                                                        postalCode: addresses?.[0]?.postalCode || ''
                                                    }
                                                }
                                            });
                                        }}
                                        className="py-2 px-4 rounded text-white font-medium flex items-center justify-center bg-orange-500 hover:bg-orange-600"
                                        aria-label="Crea Lead"
                                        title="Crea Lead"
                                    >
                                        <i className="fi fi-br-megaphone mr-2"></i>
                                        Crea Lead
                                    </button>
                                    <button
                                        onClick={handleClosePanel}
                                        className="py-2 px-4 rounded bg-gray-600 hover:bg-gray-700 text-white font-medium flex items-center justify-center"
                                        aria-label="Chiudi"
                                        title="Chiudi pannello"
                                    >
                                        <i className="fas fa-times mr-2"></i>
                                        Chiudi
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Colonna Destra - Tab con Folder */}
                    <div className="w-full xl:w-3/4 flex-1 flex flex-col h-full">
                        <CustomFolder 
                            tabTitleArray={tabTitles} 
                            palette={palette} 
                            className="flex-1 flex flex-col h-full" 
                            initialTab={activeTab}
                            onTabChange={(index) => setActiveTab(index)}
                        >
                            <div className="flex-1 overflow-y-auto">
                                {renderTabContent()}
                            </div>
                        </CustomFolder>
                    </div>
                </>
            )}
        </div>
    );
}
